import { useEffect } from 'react'
import useElement from './use-element'

interface Options<T extends HTMLElement = HTMLElement> {
  isActive: boolean
  returnFocusElement?: T
  onPressEscape?: () => void
  autoFocusDelay?: number
}

const useFocusTrap = <
  TContainerNode extends HTMLElement = HTMLElement,
  TReturnFocusNode extends HTMLElement = HTMLElement
>({
  isActive,
  returnFocusElement,
  onPressEscape,
  autoFocusDelay = 100,
}: Options<TReturnFocusNode>) => {
  const [trapContainer, trapContainerRef] = useElement<TContainerNode>()

  useEffect(() => {
    if (trapContainer) {
      const focusableElements = trapContainer.querySelectorAll<HTMLElement>(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )
      const firstElement = focusableElements[0]
      const lastElement = focusableElements[focusableElements.length - 1]

      const handleKeyPress = event => {
        if (event.key === 'Escape') {
          returnFocusElement?.focus()
          onPressEscape?.()
        } else if (event.key === 'Tab') {
          if (event.shiftKey && document.activeElement === firstElement) {
            event.preventDefault()
            lastElement.focus()
          } else if (!event.shiftKey && document.activeElement === lastElement) {
            event.preventDefault()
            firstElement.focus()
          }
        }
      }

      if (isActive) {
        setTimeout(() => {
          firstElement?.focus()
        }, autoFocusDelay)
        trapContainer.addEventListener('keydown', handleKeyPress)
      } else {
        trapContainer.removeEventListener('keydown', handleKeyPress)
      }

      return () => {
        trapContainer.removeEventListener('keydown', handleKeyPress)
      }
    }
  }, [isActive, trapContainer, returnFocusElement, onPressEscape, autoFocusDelay])

  return {
    trapContainerRef,
  }
}

export default useFocusTrap
